import Head from "next/head";
import { useRef, useState, useEffect } from "react";
import { useRouter } from "next/router";
import {
  ShoppingCartIcon,
  DownloadIcon as ProductIcon,
} from "@heroicons/react/solid";
import cx from "classnames";
import Link from "next/link";
import Image from "next/image";
import {
  DownloadIcon,
  ShareIcon,
  ReceiptTaxIcon,
} from "@heroicons/react/outline";
import useLocalStorage from "hooks/useLocalStorage";
import { Expert, TutorialItem } from "types/user";
import {
  getFullName,
  getLinkFromString,
  handleClipboard,
  truncateString,
  isFacebookSupportedImage,
  findCountryByCode,
  trackPageView,
  trackViewContent,
  cloudfront,
  toast,
  makeUrlsClickable,
} from "utils";
// import Breadcrumbs from "components/server/Breadcrumbs";
import Button from "components/Button/Button";
import OfferAttachment from "components/OfferAttachment/OfferAttachment";
import { OfferSignInUpOtp } from "components/OfferSignInUpOtp";
import { OfferSignIn } from "components/OfferSignIn";
import { HourglassIcon } from "components/Icons";
import { HeartIcon } from "@heroicons/react/outline";
import Script from "next/script";
import { useTranslation } from "react-i18next";
// import ChatIcon from "components/Icons/ChatIcon/ChatIcon";
import { useMutation } from "@tanstack/react-query";
import { api } from "utils/api";
import Modal from "components/Modal/Modal";
import ButtonPrimary from "components/Button/Button";
// import Cookies from "js-cookie";

const TutorialPage = ({
  expert,
  tutorial,
  query,
}: {
  expert: Expert;
  tutorial: TutorialItem;
  query;
}) => {
  const router = useRouter();
  const publicId = router.query.publicId as string;
  const tutorialId = router.query.tutorialId as string;
  const [hasAuthForm, setAuthForm] = useState<"sign-in" | "sign-up">(null);
  const chatSignUpRef = useRef(null);
  const chatSignInRef = useRef(null);
  const [, setTutorialBuyFlow] = useLocalStorage(`product-${tutorialId}`, null);
  const [isChatOpenTrue, setOpenChat] = useLocalStorage(
    `product-chat-${tutorialId}`,
    null
  );
  const [isFollowTrue, setFollowChat] = useLocalStorage(
    `product-follow-${tutorialId}`,
    null
  );

  const { t } = useTranslation();
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const [promo, setPromo] = useState("");
  const [code, setCode] = useState("" || router.query.promo);
  const [promotionCode, setPromotionCode] = useState<any>(undefined);

  function promoModalClose() {
    setPromo("");
    setIsPromoModalOpen(false);
  }
  const handleCheckPromotion = async () => {
    try {
      await checkPromotion({
        code: promo,
        offerid: tutorial?.offerId,
      });
    } catch (error) {
      setCode("");
      setPromo("");
      console.error("Error checking promotion:", error);
    }
  };

  const handleOpenPromoModal = () => {
    setIsPromoModalOpen(true);
  };
  const { mutateAsync: checkPromotion, isLoading: checkPromotionLoader } =
    useMutation(
      (payload: { code: string | any; offerid?: string }) =>
        api.post("/experts/v1/experts/check-promotion", payload),
      {
        onSuccess: (data) => {
          setCode(promo);
          // Cookies.set(`promo-${tutorialId}`, promo);
          // Cookies.set(`slug-${tutorialId}`, tutorial?.offerSlug);

          if (
            data?.status === "PROMOTION_NOT_FOUND" ||
            data?.status === "PROMOTION_LIMIT_REACHED" ||
            data?.status === "PROMOTION_EXPIRED" ||
            data?.status === "PROMOTION_DISABLED" ||
            data?.status === "PROMOTION_DELETED"
          ) {
            setCode("");
            toast(
              {
                title: t("promoCodeIncorrectExpired"),
                icon: "error",
              },
              { type: "custom" }
            );
          } else {
            setCode(promo);
            setPromotionCode(data);
            toast(t("promoCodeAddedToast"), { type: "success" });
            router.push(
              `/${publicId}/product/${tutorialId}` +
                (promo || code ? `?promo=${promo || code}` : "")
            );
          }
          promoModalClose();
        },
        onError: (error) => {
          setCode("");
          promoModalClose();
          toast(
            {
              title: t("promoCodeIncorrectExpired"),
              icon: "error",
            },
            { type: "custom" }
          );
        },
      }
    );

  const { mutateAsync: promotion } = useMutation(
    (payload: { code: string | any; offerid?: string }) =>
      api.post("/experts/v1/experts/check-promotion", payload),
    {
      onSuccess: (data) => {
        if (
          data?.status === "PROMOTION_NOT_FOUND" ||
          data?.status === "PROMOTION_LIMIT_REACHED" ||
          data?.status === "PROMOTION_EXPIRED" ||
          data?.status === "PROMOTION_DISABLED" ||
          data?.status === "PROMOTION_DELETED"
        ) {
          handleLeavePage();
          setCode("");
          toast(
            {
              title: t("promoCodeIncorrectExpired"),
              icon: "error",
            },
            { type: "custom" }
          );
          router.push(`/${publicId}/product/${tutorialId}`);
        } else {
          setPromotionCode(data);
          router.push(
            `/${publicId}/product/${tutorialId}` +
              (code ? `?promo=${code}` : "")
          );
        }
      },
      onError: (error) => {
        handleLeavePage();
        // Cookies.remove(`promo-${tutorialId}`);
        // Cookies.remove(`slug-${tutorialId}`);
        setCode("");
        toast(
          {
            title: t("promoCodeIncorrectExpired"),
            icon: "error",
          },
          { type: "custom" }
        );
      },
    }
  );

  useEffect(() => {
    if (code) {
      checkPromo();
    }
  }, []);

  const handleLeavePage = () => {
    const { promo, ...query } = router.query;

    router.replace(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    );
  };

  const checkPromo = async () => {
    try {
      await promotion({
        code: code,
        offerid: tutorial?.offerId,
      });
    } catch (error) {
      console.error("Error checking promotion:", error);
    }
  };

  useEffect(() => {
    if (tutorial?.metapixelid || expert?.metapixelid) {
      trackPageView(
        tutorial?.metapixelid || expert?.metapixelid,
        tutorial?.offerSlug,
        tutorial?.name
      );
    }
  }, [expert?.metapixelid, tutorial?.metapixelid]);

  useEffect(() => {
    if (tutorial) {
      trackViewContent(
        tutorial?.offerSlug,
        "product",
        tutorial?.name,
        1,
        tutorial?.price,
        tutorial?.currency,
        expert?.publicId,
        tutorial?.metapixelid || expert?.metapixelid,
        tutorial?.gtmid || expert?.gtmid
      );
    }
  }, [
    tutorial,
    expert?.publicId,
    expert?.gtmid,
    tutorial?.gtmid,
    tutorial?.metapixelid,
  ]);

  useEffect(() => {
    if (query.p === "") {
      !isChatOpenTrue || !isFollowTrue ? setTutorialBuyFlow(true) : null;
      setAuthForm("sign-up");
      if (isMobile) {
        setTimeout(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: "smooth",
          });
        }, 100);
      } else if (!isMobile) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  }, [query]);

  const handleCopy = () => {
    handleClipboard({
      text: `${publicId}/product/${tutorialId}`,
      toastText: "toastOfferUrlCopied",
    });
  };
  const openChat = () => {
    setOpenChat(true);
    setAuthForm("sign-up");
  };

  const handleFollow = () => {
    setFollowChat(true);
    setAuthForm("sign-up");
  };
  const isMobile =
    typeof window !== "undefined" &&
    /Mobi|Android/i.test(window.navigator.userAgent);

  const handleBuy = () => {
    !isChatOpenTrue || !isFollowTrue ? setTutorialBuyFlow(true) : null;
    setAuthForm("sign-up");
    if (isMobile) {
      setTimeout(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    } else if (!isMobile) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const { firstName, lastName, avatar, freeChat, isOnline, country } = expert;

  const fullName = getFullName(firstName, lastName, publicId);

  const countryCurrency = findCountryByCode(tutorial?.currency);
  const IconComponent: any = countryCurrency?.icon;

  if (!tutorial) {
    return (
      <div className="flex items-center justify-center w-full h-screen -mt-16 pt-16 text-center">
        <div className="flex flex-col items-center max-w-sm">
          <Image
            src="/img/empty-expert.svg"
            width={128}
            height={128}
            alt="no expert"
          />
          <h3 className="font-bold mt-20 text-lg">{t("emptyExpertTitle")}</h3>
          <p className="text-sm font-medium opacity-50 mt-4">
            {t("emptyExpertText")}
          </p>
        </div>
      </div>
    );
  }

  const title = `${tutorial?.name ?? ""} - ${fullName}`;
  const description = truncateString(tutorial?.description, 150);
  const transformedDescription = getLinkFromString(tutorial?.description);

  const cover = tutorial?.cover;
  const avatarOrGeneric = isFacebookSupportedImage(avatar)
    ? avatar
    : "https://waylight.me/wlgenericpreview.png";
  const isProperImage = isFacebookSupportedImage(cover);

  const gtmid = expert?.gtmid;
  const hotjarid = expert?.hotjarid;
  const metapixelid = expert?.metapixelid;
  const isSoldEnabled = expert?.isSoldEnabled;

  function calculateDiscountedPrice(originalPrice, discountPercent) {
    let discountAmount = originalPrice * (discountPercent / 100);
    let discountedPrice = originalPrice - discountAmount;
    return discountedPrice;
  }

  const discountedPrice = promotionCode
    ? Math.round(
        calculateDiscountedPrice(
          tutorial?.price,
          promotionCode.offer_discount
        ) * 100
      ) / 100
    : tutorial?.price;

  const isFreeTutorial = discountedPrice === 0 || tutorial?.price === "0.00";
  const discount = Number(tutorial?.price) - discountedPrice;
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />

        <meta
          property="og:image"
          content={
            cover
              ? isProperImage
                ? cloudfront + cover
                : avatarOrGeneric
              : avatarOrGeneric
          }
        />
        <meta
          property="og:url"
          content={`https://waylight.me/${publicId}/product/${tutorial?.offerSlug}`}
        />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
        <meta
          property="twitter:image"
          content={
            cover
              ? isProperImage
                ? cloudfront + cover
                : avatarOrGeneric
              : avatarOrGeneric
          }
        />
        <meta property="twitter:card" content="summary" />
        {tutorial?.gtmid || gtmid ? (
          <script
            dangerouslySetInnerHTML={{
              __html: `
     window.dataLayer = window.dataLayer || [];
     function gtag(){dataLayer.push(arguments);}
     gtag('js', new Date());
     gtag('config', '${tutorial?.gtmid || gtmid}');
   `,
            }}
          />
        ) : null}
      </Head>

      {tutorial?.gtmid || gtmid ? (
        <Script
          id="profilegtm"
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                 new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                 j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                 'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                 })(window,document,'script','dataLayer','${
                   tutorial?.gtmid || gtmid
                 }');`,
          }}
        />
      ) : null}

      {hotjarid ? (
        <Script
          id="profilehotjar"
          dangerouslySetInnerHTML={{
            __html: `(function(h,o,t,j,a,r){
                           h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                           h._hjSettings={hjid:${hotjarid},hjsv:6};
                           a=o.getElementsByTagName('head')[0];
                           r=o.createElement('script');r.async=1;
                           r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                           a.appendChild(r);
                       })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
          }}
        />
      ) : null}

      {tutorial?.metapixelid || metapixelid ? (
        <Script
          id="profilemetanew"
          dangerouslySetInnerHTML={{
            __html: `!function(f,b,e,v,n,t,s)
                                       {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                                       n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                                       if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                                       n.queue=[];t=b.createElement(e);t.async=!0;
                                       t.src=v;s=b.getElementsByTagName(e)[0];
                                       s.parentNode.insertBefore(t,s)}(window, document,'script',
                                       'https://connect.facebook.net/en_US/fbevents.js');
                                       fbq('init', '${
                                         tutorial?.metapixelid || metapixelid
                                       }');
                                
                                       `,
          }}
        />
      ) : null}

      <div
        className={cx("mx-auto px-4", {
          "max-w-7xl xl:px-8": hasAuthForm,
          "max-w-3xl lg:max-w-4xl lg:px-14 pb-4": !hasAuthForm,
        })}
      >
        {/* <Breadcrumbs
          pages={[
            { name: t("explore"), url: "/" },
            {
              name: fullName,
              url: `/${publicId}#products`,
            },
            { name: t("tutorial") },
          ]}
        /> */}
        <div className="pb-6 pt-6 flex md:pb-4 flex-col md:flex-row">
          <div className="flex flex-col sm:items-start basis-full">
            <div className="flex gap-4 items-center">
              <Link href={`/${publicId}#products`} passHref>
                <div className="relative mr-0  drop-shadow-4sm cursor-pointer">
                  <img
                    alt="Avatar"
                    src={avatar}
                    className="border-2 border-white bg-avatar-gradient w-12 h-12 sm:w-[60px] sm:h-[60px] object-cover rounded-full max-w-none"
                  />
                  <span
                    className={cx(
                      "absolute z-10 top-1 right-1 inline-flex items-center justify-center p-1 text-xs font-bold leading-none rounded-full border-2 border-white",
                      {
                        "bg-red-600": !isOnline,
                        "bg-green-600": isOnline,
                      }
                    )}
                  />
                </div>
              </Link>
              <h2 className="break-words max-w-full text-lg font-semibold leading-5 text-slate-900 sm:text-2xl">
                {tutorial?.name}
              </h2>
            </div>

            <div className="py-3 flex flex-wrap gap-x-3 gap-y-1 font-medium text-sm text-slate-500">
              {promotionCode && (
                <span className="flex items-center first-letter:uppercase">
                  <ReceiptTaxIcon className="w-5 h-5 mr-1 text-custom-dark-blue stroke-2" />{" "}
                  <span className="font-semibold text-custom-dark-blue">
                    {promotionCode.code}
                  </span>
                </span>
              )}
              <span className="flex items-center first-letter:uppercase">
                <ProductIcon className="w-5 h-5 mr-1" />{" "}
                <span className="first-letter:uppercase">{t("tutorial")}</span>
              </span>
              <span className="flex items-center whitespace-nowrap">
                <IconComponent className="shrink-0 w-5 h-5 mr-1 bg-slate-500 text-white" />
                <span
                  className={
                    promotionCode && discount !== 0 ? "line-through" : ""
                  }
                >
                  {tutorial?.price}
                </span>
                {promotionCode && discount !== 0 ? (
                  <span className="ml-1">
                    {discountedPrice === 0 ? "0.00" : discountedPrice}
                  </span>
                ) : null}
              </span>
              {tutorial?.expiration ? (
                <span className="flex items-center whitespace-nowrap text-slate-500">
                  <HourglassIcon className="shrink-0 w-5 h-5" />
                  <span className="ml-1">
                    {tutorial?.expiration} {t("days")}
                  </span>
                </span>
              ) : null}
              {isSoldEnabled ? (
                <>
                  <span className="flex items-center custom-dark-blue">
                    <ShoppingCartIcon className="w-5 h-5 mr-1 text-custom-dark-blue" />
                    <span className="flex items-center custom-dark-blue">
                      {tutorial?.sold}
                    </span>
                  </span>
                </>
              ) : null}
            </div>
          </div>
          <div className="flex basis-auto flex-row sm:flex-row sm:shrink-0 items-start gap-2">
            <Button
              onClick={handleFollow}
              type="rounded"
              className=""
              icon={<HeartIcon className="text-slate-500 h-4 w-4" />}
            />
            {/* <Button
              icon={<ChatIcon className="stroke-slate-500 h-4 w-4" />}
              type="rounded"
              onClick={openChat}
              className={
                freeChat ? "!border-slate-500 text-slate-900 mr-0" : "hidden"
              }
            /> */}
            <Button
              icon={<ShareIcon className="text-slate-500 h-4 w-4" />}
              type="rounded"
              onClick={handleCopy}
              className="!border-slate-500 text-slate-900 clipboard"
            />
          </div>
        </div>
        <div className="lg:grid grid-cols-12 gap-4">
          <div
            className={cx("bg-slate-50 p-4 shadow rounded-xl mb-4", {
              "col-span-8": hasAuthForm,
              "col-span-12": !hasAuthForm,
            })}
          >
            <div className="text-sm mb-4 whitespace-pre-line break-words">
              {makeUrlsClickable(tutorial?.description)}
            </div>

            <OfferAttachment
              attachemntUrl={
                tutorial?.cover !== null ? cloudfront + tutorial?.cover : null
              }
              adaptivePlaylistCover={tutorial?.adaptivePlaylistCover}
              cover={true}
            />

            {hasAuthForm ? null : (
              <>
                <div className="mx-auto w-[300px] mt-4">
                  <Button
                    icon={<DownloadIcon className="h-6 w-6" />}
                    type="danger"
                    onClick={handleBuy}
                    data-testid="buy-button"
                    fluid
                  >
                    {isFreeTutorial
                      ? t("addForFree")
                      : `${t("buyFor")} ${
                          countryCurrency?.symbol
                        }${discountedPrice}`}
                  </Button>
                </div>
                {!promotionCode ? (
                  <div className="flex justify-center cursor-pointer text-custom-dark-blue mt-4 text-sm">
                    <button onClick={handleOpenPromoModal}>
                      + {t("enterPromoCodePromotions")}
                    </button>
                  </div>
                ) : null}
              </>
            )}
          </div>
          {hasAuthForm ? (
            <div className="mx-auto col-span-4 rounded-xl sm:rounded-lg lg:shadow md:p-4 lg:p-4 max-w-md lg:min-w-full mb-auto">
              {hasAuthForm === "sign-up" ? (
                <OfferSignInUpOtp
                  type="product"
                  handleAuthForm={setAuthForm}
                  emailRef={chatSignUpRef}
                  publicId={publicId}
                  offerId={tutorialId}
                  code={code}
                />
              ) : (
                <OfferSignIn
                  type="product"
                  handleAuthForm={setAuthForm}
                  emailRef={chatSignInRef}
                  publicId={publicId}
                  offerId={tutorialId}
                />
              )}
            </div>
          ) : null}
          <div className="mx-auto md:hidden py-4 px-4 md:px-6 h-[205px]" />
        </div>
      </div>
      <Modal
        isOpen={isPromoModalOpen}
        showCloseBtn={true}
        title={t("enterPromoCodePromotions")}
        icon="promotion"
        onClose={promoModalClose}
      >
        <div className="relative mb-6">
          <ReceiptTaxIcon className="absolute left-3 top-[12px] transform -t-y-1/2 h-5 w-5 text-slate-500" />
          <input
            className="shadow appearance-none border border-slate-300 text-gray-900 rounded w-full py-3 pl-10 pr-3 text-sm font-semibold leading-tight focus:outline-none focus:shadow-outline"
            id="promo-code"
            type="text"
            value={promo}
            onChange={(e) => setPromo(e.target.value)}
          />
        </div>
        <div className="flex justify-center gap-4 mx-auto">
          <Button
            type="secondary"
            onClick={promoModalClose}
            className="w-40 md:w-50"
          >
            {t("buttonCancel")}
          </Button>

          <ButtonPrimary
            fluid
            type="primary"
            onClick={handleCheckPromotion}
            className="w-40 md:w-50"
            loading={checkPromotionLoader}
          >
            {t("confirm")}
          </ButtonPrimary>
        </div>
      </Modal>
    </>
  );
};

export default TutorialPage;
